import { AtSign, Bell, CheckCircle, MessageSquare, Trash2, UserPlus } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNotification, NotificationType } from '../types/notification';
import { Button } from './ui/button';

const ICON_MAP: Record<NotificationType, React.ReactNode> = {
  TASK_ASSIGNED: <UserPlus className="h-4 w-4 text-blue-500" />,
  TASK_STATUS_CHANGED: <CheckCircle className="h-4 w-4 text-amber-500" />,
  TASK_COMMENT_ADDED: <MessageSquare className="h-4 w-4 text-green-500" />,
  TASK_MENTIONED: <AtSign className="h-4 w-4 text-purple-500" />,
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

interface Props {
  notification: AppNotification;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem: React.FC<Props> = ({ notification, onRead, onDelete }) => {
  const navigate = useNavigate();
  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isUnread) onRead(notification.id);
    if (notification.data?.task_id) {
      navigate(`/tasks/${notification.data.task_id}`);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${
        isUnread ? 'bg-blue-50/50' : ''
      }`}
    >
      <div className="mt-0.5 shrink-0">
        {ICON_MAP[notification.type] ?? <Bell className="h-4 w-4 text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{notification.message}</p>
        <p className="text-xs text-gray-400 mt-1">{timeAgo(notification.created_at)}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isUnread && <span className="h-2 w-2 rounded-full bg-blue-500" />}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
          onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default NotificationItem;
